import { useState } from "react"
import { ThumbsDown, ThumbsUp } from "lucide-react"
import { useIntl } from "react-intl"
import { Button } from "@/components/ui/button"
import { csrfToken } from "@/lib/csrf"

type State = "asking" | "commenting" | "saving" | "done"

/**
 * "Was this explanation helpful?" shown next to a question's explanation.
 * A "no" invites an optional note. Votes go to a JSON endpoint with fetch(),
 * so rating never reloads the page the user is studying on.
 */
export function HelpfulPrompt({ attemptId, questionId }: { attemptId: number; questionId: number }) {
  const intl = useIntl()
  const [state, setState] = useState<State>("asking")
  const [helpful, setHelpful] = useState<boolean | null>(null)
  const [comment, setComment] = useState("")
  const [error, setError] = useState<string | null>(null)

  const send = async (vote: boolean, note = "") => {
    setState("saving")
    setError(null)
    try {
      const response = await fetch(`/attempts/${attemptId}/questions/${questionId}/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ helpful: vote, comment: note }),
      })
      if (response.ok) {
        setHelpful(vote)
        setState("done")
        return
      }
      const data = await response.json().catch(() => null)
      setError(data?.error ?? intl.formatMessage({ id: "quizzes.rating_error" }))
    } catch {
      setError(intl.formatMessage({ id: "quizzes.rating_error" }))
    }
    setState(helpful === false || vote === false ? "commenting" : "asking")
  }

  const vote = (value: boolean) => {
    if (value) {
      send(true)
    } else {
      setHelpful(false)
      setState("commenting")
    }
  }

  if (state === "done") {
    return (
      <p className="text-sm text-ink-muted" role="status">
        {intl.formatMessage({ id: "quizzes.thanks" })}
      </p>
    )
  }

  if (state === "commenting" || (state === "saving" && helpful === false)) {
    return (
      // Not a <form>: this renders inside the quiz page's own form, and a nested submit would bubble up to it.
      <div className="space-y-2">
        <label htmlFor={`helpful-comment-${questionId}`}>{intl.formatMessage({ id: "quizzes.better_prompt" })}</label>
        <textarea
          id={`helpful-comment-${questionId}`}
          className="form-control form-control-textarea"
          rows={3}
          maxLength={1000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        {error && (
          <p role="alert" className="text-xs text-danger-display">
            {error}
          </p>
        )}
        <Button type="button" size="sm" disabled={state === "saving"} onClick={() => send(false, comment)}>
          {intl.formatMessage({ id: "quizzes.send" })}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={intl.formatMessage({ id: "quizzes.helpful_group" })}>
      <span className="text-sm text-ink-muted">{intl.formatMessage({ id: "quizzes.was_helpful" })}</span>
      <Button type="button" variant="secondary" size="sm" disabled={state === "saving"} onClick={() => vote(true)}>
        <ThumbsUp className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.answer_yes" })}
      </Button>
      <Button type="button" variant="secondary" size="sm" disabled={state === "saving"} onClick={() => vote(false)}>
        <ThumbsDown className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.answer_no" })}
      </Button>
      {error && (
        <span role="alert" className="text-xs text-danger-display">
          {error}
        </span>
      )}
    </div>
  )
}
